import { type PropsWithChildren, type ReactElement } from "react";
import {
  Animated as AnimatedNative,
  FlatListProps,
  Platform,
  StyleSheet,
  View,
} from "react-native";
import Animated, {
  interpolate,
  useAnimatedRef,
  useAnimatedStyle,
  useScrollViewOffset,
} from "react-native-reanimated";
import { Shadow } from "react-native-shadow-2";
import CustomFlatList from "./flatlist";

const HEADER_HEIGHT = 100;

const AnimatedCustomFlatList = Animated.createAnimatedComponent(CustomFlatList);

type Props = PropsWithChildren<{
  header: ReactElement;
  hederHeight?: number;
  scrollRef?: any;
  maxItemsPerRow?: number;
  superGrid?: boolean;
}>;

export default function ParallaxScrollView({
  children,
  header,
  hederHeight = HEADER_HEIGHT,
  scrollRef,
  ...rest
}: Props & FlatListProps<any>) {
  const scrollRefInternal = useAnimatedRef();
  const scrollOffset = useScrollViewOffset(scrollRefInternal as any);
  const extraProps = {};
  if (scrollRef) {
    extraProps["onScroll"] = AnimatedNative.event(
      [
        {
          nativeEvent: {
            contentOffset: {
              y: scrollRef,
            },
          },
        },
      ],
      { useNativeDriver: false }
    );
  }

  const headerAnimatedStyle = useAnimatedStyle<any>(() => {
    return {
      transform: [
        {
          translateY: interpolate(
            scrollOffset.value,
            [-hederHeight, 0, hederHeight],
            [-hederHeight / 2, 0, hederHeight / 3]
          ),
        },
        {
          scale: interpolate(
            scrollOffset.value,
            [-hederHeight, 0, hederHeight],
            [1, 1, 1]
          ),
        },
      ],
    };
  });

  const shadowDistanceStyle = useAnimatedStyle<any>(() => {
    return {
      opacity: interpolate(
        scrollOffset.value,
        [-hederHeight, 0, 50],
        [0, 0, 1]
      ),
    };
  });

  return (
    <View style={styles.container}>
      <AnimatedCustomFlatList
        ref={scrollRefInternal}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}
        keyExtractor={(item) => item._id}
        ListHeaderComponent={
          <>
            <Animated.View style={[styles.header, headerAnimatedStyle]}>
              {header}
            </Animated.View>
            <Animated.View style={{ ...shadowDistanceStyle }}>
              <Shadow distance={10} offset={[3, 4]}>
                <View style={{ width: "100%" }} />
              </Shadow>
            </Animated.View>
          </>
        }
        {...rest}
        // {...extraProps}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    overflow: "hidden",
  },
  content: {
    flex: 1,
    overflow: "hidden",
  },
});
